import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { User } from "models/User";
import React, { useState } from "react";
import { Card, CardBody } from "reactstrap";
import { setupUserMfa } from "services/Api";
import { apiErrorMessage } from "services/ApiError";

interface Props {
    user: User;
    canManage: boolean;
    onConfigured?: () => Promise<unknown> | void;
}

const ManagedUserMfaCard: React.FC<Props> = ({ user, canManage, onConfigured }) => {
    const { t } = useOfficeTranslation();
    const [mfaQr, setMfaQr] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const hasTotp = (user.mfa_methods?.includes("totp") ?? false) || mfaQr !== null;

    const showMfaQr = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await setupUserMfa(user.id);
            if (!result.mfa_qr) throw new Error("missing MFA QR code");
            setMfaQr(result.mfa_qr);
            void Promise.resolve(onConfigured?.()).catch(() => undefined);
        } catch (reason) {
            setError(apiErrorMessage(reason, t("user.security.mfaQrFailed")));
        } finally {
            setLoading(false);
        }
    };

    return <Card><CardBody>
        <h5 className="mb-3">{t("user.userDetail.security")}</h5>
        <div className="d-flex align-items-center justify-content-between mb-3">
            <span>{t("user.userDetail.mfaStatus")}</span>
            <span className={`badge ${user.mfa_active ? "bg-success bg-soft text-success" : "bg-warning bg-soft text-warning"}`}>
                <i className={`mdi ${user.mfa_active ? "mdi-shield-check" : "mdi-shield-alert"} me-1`} />
                {t(user.mfa_active ? "common.status.active" : "common.status.inactive")}
            </span>
        </div>
        <div className="d-flex align-items-center justify-content-between">
            <span>{t("user.userDetail.mfaMethod")}</span>
            <strong>{t(hasTotp ? "user.security.authenticatorApp" : "common.none")}</strong>
        </div>

        {canManage && !user.mfa_active && (!hasTotp || mfaQr) && <div className="mt-3 pt-3 border-top">
            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            {!mfaQr && !hasTotp && <button type="button" className="btn btn-primary w-100" disabled={loading} onClick={showMfaQr}>
                <i className="mdi mdi-qrcode me-1" />
                {t(loading ? "user.security.creatingMfaQrCode" : "user.security.createMfaQrCode")}
            </button>}
            {mfaQr && <div className="text-center">
                <div className="alert alert-info text-start" role="status">{t("user.security.mfaQrReady")}</div>
                <div className="d-inline-block bg-white border rounded p-2">
                    <img
                        src={`data:image/png;base64,${mfaQr}`}
                        alt={t("user.security.mfaQrCodeAlt", { name: user.name })}
                        width={240}
                        height={240}
                        className="img-fluid d-block"
                    />
                </div>
                <div className="mt-3">
                    <a
                        className="btn btn-outline-primary"
                        href={`data:image/png;base64,${mfaQr}`}
                        download={`mfa-${user.email}.png`}
                    >
                        <i className="mdi mdi-download me-1" />{t("user.security.downloadMfaQrCode")}
                    </a>
                </div>
            </div>}
        </div>}
    </CardBody></Card>;
};

export default ManagedUserMfaCard;
